<script lang="ts">
	import { Auth } from "@utils/Auth.svelte.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import type { ClassValue } from "svelte/elements";
	import arrowIcon from "@core/assets/icons/arrow.svg?raw";
	import padlockIcon from "@core/assets/icons/padlock.svg?raw";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import type { PublishedPost } from "@core/schema/Post.js";
	import { getPostDetailPath } from "@core/schema/paths/getPostDetailPath.js";
	import { transformHtmlToText } from "@core/schema/utils/transformHtmlToText.js";
	import translations from "./ArticleCard.translations.json" with { type: "json" };
	import Logo from "./Logo.svelte";
	import PostPublishedDate from "./PostPublishedDate.svelte";
	import { ButtonSize } from "./common/ButtonSize.js";
	import Formatted from "./common/Formatted.svelte";
	import Heading from "./common/Heading.svelte";
	import type { HeadingLevel } from "./common/HeadingLevel.js";
	import Icon from "./common/Icon.svelte";
	import Link from "./common/Link.svelte";
	import Media from "./common/Media.svelte";

	const translate = getTranslate(translations);

	const {
		post,
		headingLevel,
		huge,
		lcp,
		class: customClass,
	}: { post: PublishedPost; huge?: boolean; headingLevel: HeadingLevel; class?: ClassValue; lcp?: boolean } = $props();

	/* eslint-disable @typescript-eslint/no-confusing-void-expression */
</script>

{#snippet media()}
	<div class={["relative", huge && "lg:h-full lg:aspect-[3/2] lg:grow"]}>
		{#if post.primaryImage}
			<Media
				alt={post.primaryImage.title ?? ""}
				source={post.primaryImage}
				width={343}
				height={320}
				sourcesets={{
					"<sm": huge ? [0, 900] : [0, 600],
					">sm": huge ? [0, 900] : [0, 600],
				}}
				wrapperClass="rounded-2xl overflow-hidden mb-4 size-full aspect-[171/115] max-w-full"
				class="object-cover size-full bg-fcrdarkbrown"
				eager={lcp}
				preload={lcp ? "high" : undefined}
			/>
		{:else}
			<div
				class="rounded-2xl mb-4 size-full aspect-[171/115] max-w-full bg-fcrdarkbrown flex justify-center items-center"
			>
				<Logo class="text-white w-1/3" />
			</div>
		{/if}
		{#if post.ffcMembersOnly && !Auth.isFfcMember}
			<div
				class="absolute top-2 right-2 flex size-8 items-center justify-center rounded-full bg-fcrdarkgray/80"
				aria-label={translate("ffcMembersOnly")}
			>
				<Icon icon={padlockIcon} class="size-4 text-white" />
			</div>
		{/if}
	</div>
{/snippet}

{#snippet inner()}
	{#if huge}
		<a href={getPostDetailPath(post)} class="contents cursor-pointer">
			{@render media()}
		</a>
	{:else}
		{@render media()}
	{/if}
	<div class={["flex flex-col gap-2", huge && "lg:max-w-97 shrink-0"]}>
		<PostPublishedDate {post} />
		<Heading
			level={headingLevel}
			text={huge ? `[${getPostDetailPath(post)}][[${post.title}]]` : post.title}
			class={[
				"font-antipol text-fcrdarkgray transition-colors mt-2",
				huge ? "leading-tighter text-3.5xl sm:text-4.5xl" : "text-base sm:text-1.5xl",
				huge
					? "hover:text-fcrbrown cursor-pointer hover:underline"
					: "group-hover/product-card:text-fcrbrown group-hover/product-card:underline",
			]}
		/>
		{#if huge}
			<div class="flex flex-col justify-between grow">
				<p class="text-sm/normal sm:text-base md:text-base text-fcrgray line-clamp-3">
					<Formatted text={transformHtmlToText(post.perex)} />
				</p>
				<Link
					href={getPostDetailPath(post)}
					aria-label={translate("goToDetail")}
					variant={ButtonVariant.Outlined}
					size={ButtonSize.Medium}
					class="!size-16 items-center justify-center hidden lg:flex"
				>
					<Icon icon={arrowIcon} class="size-4.5" />
				</Link>
			</div>
		{:else}
			<p class="text-sm/normal sm:text-base md:text-base text-fcrgray line-clamp-3">
				<Formatted text={transformHtmlToText(post.perex)} />
			</p>
		{/if}
	</div>
{/snippet}

<article
	class={[customClass, "flex flex-col gap-y-2 gap-x-16 sm:min-w-85.5 group/product-card", huge && "lg:flex-row group"]}
>
	{#if huge}
		{@render inner()}
	{:else}
		<a href={getPostDetailPath(post)} class="contents cursor-pointer">
			{@render inner()}
		</a>
	{/if}
</article>
